import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ShieldCheckIcon,
  HeartIcon,
  SparklesIcon,
  BuildingLibraryIcon,
  UserGroupIcon,
  GlobeAmericasIcon,
  ArrowTrendingUpIcon,
  HandRaisedIcon,
  ChartBarIcon,
  HomeIcon,
  DevicePhoneMobileIcon,
  AcademicCapIcon,
  BanknotesIcon,
  LightBulbIcon,
  ScaleIcon,
} from '@heroicons/react/24/outline';
import BackgroundTexture from '../components/common/BackgroundTexture';
import Footer from '../components/common/Footer';
import Navigation from '../components/common/Navigation';
import Particles from '../components/common/Particles';

const FoundingSvg = ({ className }) => (
  <svg className={className} viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="150" fill="#123524" fillOpacity="0.05"/>
    <path d="M60 110H140M70 90V110M130 90V110M75 70H125M85 50H115" 
      stroke="#123524" strokeWidth="4" strokeLinecap="round"/>
    <path d="M70 30H130L140 50V110H60V50L70 30Z" 
      stroke="#123524" strokeWidth="4" fill="none"/>
  </svg>
);

const CommunityGrowthSvg = ({ className }) => (
  <svg className={className} viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="150" fill="#123524" fillOpacity="0.05"/>
    <path d="M50 110H150M60 80L80 60L100 80L120 60L140 80V110H60V80Z" 
      stroke="#123524" strokeWidth="4" fill="none"/>
    <path d="M90 110V90H110V110M70 50L100 30L130 50L100 70L70 50Z" 
      stroke="#123524" strokeWidth="4" fill="none"/>
  </svg>
);

const DigitalInnovationSvg = ({ className }) => (
  <svg className={className} viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="150" fill="#123524" fillOpacity="0.05"/>
    <rect x="40" y="30" width="120" height="80" rx="4" stroke="#123524" strokeWidth="4" fill="none"/>
    <path d="M40 50H160M70 70L90 90L110 70L130 90" stroke="#123524" strokeWidth="4" strokeLinecap="round"/>
  </svg>
);

const StandingStrongSvg = ({ className }) => (
  <svg className={className} viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="150" fill="#123524" fillOpacity="0.05"/>
    <path d="M100 30L140 70L100 110L60 70L100 30Z" stroke="#123524" strokeWidth="4" fill="none"/>
    <path d="M85 65H115M85 75H115M85 85H115" stroke="#123524" strokeWidth="4" strokeLinecap="round"/>
  </svg>
);

const MobileRevolutionSvg = ({ className }) => (
  <svg className={className} viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="150" fill="#123524" fillOpacity="0.05"/>
    <rect x="70" y="20" width="60" height="110" rx="8" stroke="#123524" strokeWidth="4" fill="none"/>
    <line x1="70" y1="40" x2="130" y2="40" stroke="#123524" strokeWidth="4"/>
    <circle cx="100" cy="110" r="6" fill="#123524"/>
  </svg>
);

const FutureForwardSvg = ({ className }) => (
  <svg className={className} viewBox="0 0 200 150" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="200" height="150" fill="#123524" fillOpacity="0.05"/>
    <path d="M40 110L80 70L100 90L140 50" stroke="#123524" strokeWidth="4" strokeLinecap="round"/>
    <circle cx="60" cy="90" r="4" fill="#123524"/>
    <circle cx="90" cy="60" r="4" fill="#123524"/>
    <circle cx="120" cy="80" r="4" fill="#123524"/>
    <circle cx="150" cy="40" r="4" fill="#123524"/>
  </svg>
);

const timelineEvents = [
  {
    year: '1973',
    title: 'A Vision of Community Banking',
    description: 'Founded by local educators who believed in the power of cooperative banking, Addition began with a mission to provide fair, accessible financial services to teachers and their families.',
    icon: BuildingLibraryIcon,
  },
  {
    year: '1985',
    title: 'Expanding Our Family',
    description: 'As our reputation for personalized service grew, we opened our membership to all community members. This expansion allowed us to introduce innovative home loans and small business programs.',
    icon: HomeIcon,
  },
  {
    year: '1995',
    title: 'Pioneering Digital Banking',
    description: 'We embraced the digital revolution early, becoming one of the first credit unions to offer online banking. This forward-thinking approach set the foundation for our future innovations.',
    icon: SparklesIcon,
  },
  {
    year: '2008',
    title: 'Supporting Through Crisis',
    description: 'During the financial crisis, we stood by our members when they needed us most. Our mortgage assistance program helped over 1,000 families keep their homes, demonstrating our unwavering commitment.',
    icon: ShieldCheckIcon,
  },
  {
    year: '2015',
    title: 'Banking at Your Fingertips',
    description: "Our award-winning mobile app revolutionized how members interact with their finances. Features like instant mobile deposits and real-time alerts made banking more convenient than ever.",
    icon: DevicePhoneMobileIcon,
  },
  {
    year: '2023',
    title: 'Embracing the Future',
    description: 'Celebrating our golden anniversary with over 100,000 members, we launched AI-powered financial advisory services while maintaining our personal touch. Our commitment to innovation continues.',
    icon: ChartBarIcon,
  }
];

const UserAvatar = ({ className }) => (
  <svg 
    className={className} 
    viewBox="0 0 48 48" 
    fill="none" 
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle cx="24" cy="24" r="24" fill="#123524" fillOpacity="0.1"/>
    <circle cx="24" cy="17" r="8" fill="#123524"/>
    <path 
      d="M41 42.9999C41 35.2679 33.2843 29 24 29C14.7157 29 7 35.2679 7 42.9999" 
      fill="#123524"
    />
  </svg>
);

const AboutPage = () => {
  const [counters, setCounters] = useState({
    members: 0,
    assets: 0,
    satisfaction: 0,
    branches: 0,
    communityImpact: 0
  });

  useEffect(() => {
    const interval = setInterval(() => {
      setCounters(prev => ({
        members: Math.min(prev.members + 1000, 100000),
        assets: Math.min(prev.assets + 50, 2500),
        satisfaction: Math.min(prev.satisfaction + 1, 98),
        branches: Math.min(prev.branches + 1, 25),
        communityImpact: Math.min(prev.communityImpact + 0.1, 5.0)
      }));
    }, 20);

    return () => clearInterval(interval);
  }, []);

  // Testimonials data
  const testimonials = [
    {
      quote: "When I lost my job during the pandemic, Addition worked with me to defer my mortgage payments. They showed me that banking can be human.",
      author: "Michael Rodriguez",
      role: "Member since 2015"
    },
    {
      quote: "Addition helped me start my dream café. They're not just my bank, they're my partners in success.",
      author: "Sarah Chen",
      role: "Local Business Owner"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 relative">
      <BackgroundTexture />
      <Particles />
      <Navigation />

      {/* Hero Section */}
      <div className="relative pt-24 overflow-hidden bg-gradient-to-br from-primary-600 to-primary-800">
        <div className="absolute inset-0 bg-pattern-dots opacity-10"></div>
        <div className="absolute inset-0 bg-gradient-radial from-primary-400/20 via-transparent to-transparent"></div>
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="max-w-4xl mx-auto text-center relative">
            <div className="absolute -top-20 -left-20 w-40 h-40 bg-primary-300/20 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-20 -right-20 w-40 h-40 bg-primary-300/20 rounded-full blur-3xl"></div>
            
            <h1 className="text-4xl lg:text-5xl font-bold text-white mb-6 relative z-10 font-serif">
              Where Financial Strength
              <span className="block mt-2 text-accent-300">
                Meets Community Values
              </span>
            </h1>
            <p className="text-xl text-white/90 leading-relaxed max-w-2xl mx-auto mb-12">
              For 50 years, Addition Credit Union has been more than a financial institution. 
              We're a cornerstone of community growth, innovation, and trusted partnership.
            </p>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center transform hover:scale-105 transition-all duration-300">
                <div className="text-3xl font-bold text-white mb-1">
                  {counters.members.toLocaleString()}+
                </div>
                <div className="text-sm text-white/80">Members Strong</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center transform hover:scale-105 transition-all duration-300">
                <div className="text-3xl font-bold text-white mb-1">
                  ${counters.assets}M+
                </div>
                <div className="text-sm text-white/80">In Member Assets</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center transform hover:scale-105 transition-all duration-300">
                <div className="text-3xl font-bold text-white mb-1">
                  {counters.satisfaction}%
                </div>
                <div className="text-sm text-white/80">Member Satisfaction</div>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center transform hover:scale-105 transition-all duration-300">
                <div className="text-3xl font-bold text-white mb-1">
                  ${counters.communityImpact.toFixed(1)}M
                </div>
                <div className="text-sm text-white/80">Community Impact</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Mission Statement Section */}
      <div className="py-16 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 font-serif">Our Mission</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              To empower our members' financial success through personalized service, 
              innovative solutions, and unwavering commitment to community well-being. 
              We believe that when our members thrive, our community prospers.
            </p>
          </div>
        </div>
      </div>

      {/* Our Values Section */}
      <div className="py-20 bg-gradient-to-br from-primary-50 to-white relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              These fundamental principles shape every interaction, decision, and service we provide
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-white p-8 rounded-2xl shadow-soft group hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <ScaleIcon className="w-6 h-6 text-primary-600" />
                    </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Trust & Integrity</h3>
              <p className="text-gray-600">
                We maintain the highest standards of security and ethical practices, 
                ensuring your financial well-being is protected at every step.
              </p>
                  </div>

            <div className="bg-white p-8 rounded-2xl shadow-soft group hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <HeartIcon className="w-6 h-6 text-primary-600" />
                        </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Community Focus</h3>
              <p className="text-gray-600">
                Our roots in the community run deep. We invest in local growth, support 
                small businesses, and contribute to community development.
              </p>
            </div>

            <div className="bg-white p-8 rounded-2xl shadow-soft group hover:shadow-lg transition-all duration-300">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                <LightBulbIcon className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Forward Thinking</h3>
              <p className="text-gray-600">
                We embrace innovation while maintaining our personal touch, ensuring 
                our services evolve with your changing needs.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* What Sets Us Apart Section */}
      <div className="py-20 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
              The Addition Difference
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experience banking that goes beyond transactions to build lasting relationships
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-soft">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                <UserGroupIcon className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Member-Owned</h3>
              <p className="text-gray-600">
                As a credit union, our members are our shareholders. Your success is our success.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-soft">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                <BanknotesIcon className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Better Rates</h3>
              <p className="text-gray-600">
                Enjoy competitive rates on loans and higher returns on savings accounts.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-soft">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                <DevicePhoneMobileIcon className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Digital First</h3>
              <p className="text-gray-600">
                Advanced online and mobile banking with the latest security features.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-white p-6 rounded-xl shadow-soft">
              <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                <AcademicCapIcon className="w-6 h-6 text-primary-600" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Financial Education</h3>
              <p className="text-gray-600">
                Free resources and workshops to help you make informed financial decisions.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Timeline Section */}
      <div className="py-20 bg-gradient-to-br from-gray-50 to-primary-50 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">Our Journey</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Five decades of growth, innovation, and unwavering commitment to our community
              </p>
            </div>
            
          <div className="max-w-6xl mx-auto">
            {timelineEvents.map((event, index) => (
              <div key={event.year} className="relative flex items-start mb-12 last:mb-0 group">
                <div className="flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full 
                  shadow-soft flex-shrink-0 group-hover:bg-primary-200 transition-colors">
                  <event.icon className="w-8 h-8 text-primary-600" />
                        </div>
                <div className="ml-8 group-hover:translate-x-2 transition-transform duration-300">
                  <div className="flex items-center mb-2">
                    <span className="text-2xl font-bold text-primary-600 mr-4">{event.year}</span>
                    <h3 className="text-xl font-bold text-gray-900">{event.title}</h3>
                      </div>
                  <p className="text-gray-600 leading-relaxed">{event.description}</p>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>

      {/* Community Impact Section */}
      <div className="py-20 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-serif">
              Making a Difference
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our commitment to community extends beyond banking
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            <div className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-2xl shadow-soft">
              <div className="text-3xl font-bold text-primary-600 mb-2">$2M+</div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Education Grants</h3>
              <p className="text-gray-600">
                Supporting local schools and providing scholarships to help students achieve their dreams.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-2xl shadow-soft">
              <div className="text-3xl font-bold text-primary-600 mb-2">10,000+</div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Volunteer Hours</h3>
              <p className="text-gray-600">
                Our team actively participates in community service and local improvement projects.
              </p>
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-white p-8 rounded-2xl shadow-soft">
              <div className="text-3xl font-bold text-primary-600 mb-2">$500K</div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Local Business Grants</h3>
              <p className="text-gray-600">
                Supporting small businesses through grants, loans, and mentorship programs.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-20 bg-gradient-to-br from-primary-600 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern-dots opacity-10"></div>
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-6 font-serif">
              Join Our Financial Family
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Experience the difference of member-focused banking. Join Addition today 
              and become part of a community dedicated to your financial success.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link
                to="/register"
                className="px-6 py-3 bg-white text-primary-600 font-medium rounded-xl 
                  hover:bg-white/90 transition-all duration-300 hover:-translate-y-1 
                  inline-flex items-center justify-center group shadow-soft"
              >
                Become a Member
                <ArrowTrendingUpIcon className="ml-2 h-5 w-5 transform group-hover:translate-x-1 
                  transition-transform duration-200" />
              </Link>
              <Link
                to="/contact"
                className="px-6 py-3 border-2 border-white text-white font-medium 
                  rounded-xl hover:bg-white/10 transition-all duration-300 
                  hover:-translate-y-1"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AboutPage; 